import { getCurrentUser } from 'vuefire'
import type { Notification } from '~/shared/types/notification.js'
import { useMembersStore } from '~/stores/members.js'
import { useNotificationsStore } from '~/stores/notifications.js'

/**
 * Core composable for low-level notification operations
 * Provides direct access to notification CRUD operations
 */
export function useNotificationCore() {
  const notificationsStore = useNotificationsStore()
  const membersStore = useMembersStore()

  // Get current user ID from Firebase Auth using VueFire
  const getCurrentUserId = async (): Promise<string | null> => {
    const user = await getCurrentUser()
    return user?.uid || null
  }

  // Get current member from store
  const getCurrentMember = async () => {
    const userId = await getCurrentUserId()
    if (!userId) return null

    return await membersStore.fetchMember(userId)
  }

  /**
   * Create a notification for the current user
   * @param notification Notification data to create
   * @returns ID of the created notification
   */
  const createNotification = async (notification: Partial<Notification>): Promise<string> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      throw new Error('Cannot create notification: No authenticated user')
    }

    return notificationsStore.createNotification(userId, notification)
  }

  /**
   * Create a notification for a specific member
   * @param memberId ID of the member to create the notification for
   * @param notification Notification data to create
   * @returns ID of the created notification
   */
  const createNotificationForMember = async (
    memberId: string,
    notification: Partial<Notification>,
  ): Promise<string> => {
    return notificationsStore.createNotification(memberId, notification)
  }

  /**
   * Mark a notification as read
   * @param notificationId ID of the notification to mark as read
   * @returns Success status
   */
  const markAsRead = async (notificationId: string): Promise<boolean> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      throw new Error('Cannot mark notification as read: No authenticated user')
    }

    return notificationsStore.markAsRead(notificationId, userId)
  }

  /**
   * Mark all notifications as read for the current user
   * @returns Success status
   */
  const markAllAsRead = async (): Promise<boolean> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      throw new Error('Cannot mark all notifications as read: No authenticated user')
    }

    return notificationsStore.markAllAsRead(userId)
  }

  /**
   * Delete a notification
   * @param notificationId ID of the notification to delete
   * @returns Success status
   */
  const deleteNotification = async (notificationId: string): Promise<boolean> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      throw new Error('Cannot delete notification: No authenticated user')
    }

    return notificationsStore.deleteNotification(notificationId, userId)
  }

  /**
   * Subscribe to notifications for the current user
   * @returns Function to unsubscribe
   */
  const subscribeToNotifications = async (): Promise<(() => void) | undefined> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      console.warn('Cannot subscribe to notifications: No authenticated user')
      return undefined
    }

    return notificationsStore.subscribeToNotifications(userId)
  }

  /**
   * Get all notifications for the current user
   * @param limit Maximum number of notifications to fetch
   * @returns Array of notifications
   */
  const fetchNotifications = async (limit?: number): Promise<Notification[]> => {
    const userId = await getCurrentUserId()
    if (!userId) {
      throw new Error('Cannot fetch notifications: No authenticated user')
    }

    return notificationsStore.fetchNotifications(userId, limit)
  }

  return {
    createNotification,
    createNotificationForMember,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    subscribeToNotifications,
    fetchNotifications,
    getCurrentMember,

    // Pass-through getters from the store
    notifications: computed(() => notificationsStore.notificationsArray),
    unreadNotifications: computed(() => notificationsStore.unreadNotifications),
    unreadCount: computed(() => notificationsStore.unreadCount),
    loading: computed(() => notificationsStore.loading),
    getNotificationsByCategory: (category: string) =>
      notificationsStore.getNotificationsByCategory(category),
  }
}
